import React from "react";

import ReconciliationCreditsTable from "./ReconciliationCreditsTable";
import ReconciliationPaymentsTable from "./ReconciliationPaymentsTable";
import ReconciliationAdjustmentsList from "./ReconciliationAdjustmentsList";
import ReconciliationAdjustmentModal from "./ReconciliationAdjustmentModal";
import EditReconciliationInfoModal from "./EditReconciliationInfoModal";
import EditReconciliationBankBalanceModal from "./EditReconciliationBankBalanceModal";
import ConfirmationModal from "../../../commonComponents/shared/ConfirmationModal";
import OutsideTransactionModal from "../../fundsCommon/OutsideTransactionModal";
import TransactionNoteModal from "../TransactionNoteModal";
import NavigationComponent from "../../../commonComponents/util/NavigationComponent";
import FadeOutErrorMessage from "../../../commonComponents/util/FadeOutErrorMessage";
import LoadingSpinner from "../../../commonComponents/util/LoadingSpinner";

import { formatCurrency, formatDate, formatDateForInput } from "../../../../lib/utils";
import { TransactionTypes } from "../../../../lib/constants";
import { BigNumber } from "bignumber.js";

export default class BankReconciliationStep2 extends NavigationComponent {
  constructor(props) {
    super(props);

    this.state = {
      error: false,
      showModals: {
        confirmCancellationModal: false,
        newAdjustmentModal: false,
        newOutsideTrans: false,
        transactionNoteModal: false,
        reconciliationInfo: false,
        reconciliationBankBalance: false,
      },
      transactionIdForNote: null
    };
  }

  cancel() {
    this.props.clearReconciliationDetails();
    this.props.cancel();
  }

  reloadTables = (type) => {
    const isCreditsTable = type === TransactionTypes.Deposit;

    const getData = isCreditsTable
      ? this.props.getCredits
      : this.props.getPayments;
    const { results, sort } = isCreditsTable
      ? this.props.reconciliationCredits
      : this.props.reconciliationPayments;

    getData(true, 1, results, sort);
  }

  getClearedTransactionsWithNotes = () => {
    const clearedTransactions = {};

    [
      ...this.props.reconciliationPayments.selected.map(
        trans => trans.transactionId
      ),
      ...this.props.reconciliationCredits.selected.map(
        trans => trans.transactionId
      )
    ].forEach(transactionId => {
      clearedTransactions[transactionId] =
        this.props.report.transactionNotes[transactionId] || null;
    });

    return clearedTransactions;
  };

  async update(report) {
    //if dateTo is changed, check if any payments/credits selected have a date greater then dateTo
    if(report.dateTo !== this.props.report.dateTo) {
      report.dateTo =  formatDate(report.dateTo + " 00:00:0000");
      let invalidSelectedPayments = this.props.reconciliationPayments.selected.some(r => new Date(r.date) > new Date(report.dateTo));
      let invalidSelectedCredits = this.props.reconciliationCredits.selected.some(r => new Date(r.date) > new Date(report.dateTo));
      
      if(invalidSelectedPayments || invalidSelectedCredits) {
        this.setState({ invalidReportDate: true });
        return;
      }
      else {
        this.setState({ invalidReportDate: false });
        await this.props.updateReport(report);
        this.props.getPayments();
        this.props.getCredits();
      }
    }
    else {
      this.props.updateReport(report);
    }
    this.setState({showModals: {...this.state.showModals, reconciliationInfo: false, reconciliationBankBalance: false}});
  }

  async save(closeOnSave) {
    this.setState({ error: false });
    const recId = this.props.report.id || this.props.newReconciliationReportId;

    if (recId) {
      await this.props.updateReconciliationReport(
        this.props.fundId,
        recId,
        this.getClearedTransactionsWithNotes(),
        this.props.reconciliationAdjustments,
        this.props.report,
        closeOnSave
      );
    } else {
      await this.props.createReconciliationReport(this.props.fundId, {
        ...this.props.report,
        inProgress: true,
        clearedTransactions: this.getClearedTransactionsWithNotes(),
        adjustments: this.props.reconciliationAdjustments
      },
      closeOnSave);
    }

    if (!this.props.success) {
      this.setState({ error: true });
    } else {
      if(closeOnSave) {
        this.cancel();
      }
    }
  }

  async reconcile() {
    this.setState({ error: false });
    const recId = this.props.report.id || this.props.newReconciliationReportId;

    if (recId) {
      await this.props.completeReconciliationReport(
        this.props.fundId,
        recId,
        this.getClearedTransactionsWithNotes(),
        this.props.reconciliationAdjustments,
        this.props.report
      );
    } else {
      await this.props.createAndCompleteReconciliationReport(this.props.fundId, {
        ...this.props.report,
        inProgress: false,
        clearedTransactions: this.getClearedTransactionsWithNotes(),
        adjustments: this.props.reconciliationAdjustments
      });
    }

    if (!this.props.success) {
      this.setState({ error: true });
    } else {
      this.cancel();
    }
  }

  render() {
    let difference = new BigNumber(this.props.reconciliationBalance || 0).minus(
      this.props.report.statementBalance || 0
    );
    const enabled = difference.isEqualTo(new BigNumber(0));
    difference = difference.toFixed(2); //can't chain this on above line since it calls .toFixed on the statement balance then...
    let totalPayments = this.props.reconciliationPayments.selected.reduce(
      (total, trans) => total.plus(trans.amount),
      new BigNumber(0)
    );
    let totalCredits = this.props.reconciliationCredits.selected.reduce(
      (total, trans) => total.plus(trans.amount),
      new BigNumber(0)
    );
    
    return (
      <div>
        <div className="bordered-modal-header">
          <div>
            <h4 className="page-header">{`Reconcile - ${
              this.props.fundName
            }`}</h4>
            <p className="small-text">
              {this.props.report.reportName}{" "}
              <span className="accent-text">From</span>{" "}
              {formatDate(this.props.report.dateFrom)}{" "}
              <span className="accent-text">to</span>{" "}
              {formatDate(this.props.report.dateTo.length > 10 ? this.props.report.dateTo : new Date(this.props.report.dateTo + " 00:00:0000"))}
              <i className="icon-pencil primary-hover input" onClick={this.toggleModal.bind(this, "reconciliationInfo")} style={{marginLeft: "10px"}}></i>
            </p>
          </div>
          <i
            className="icon ion-ios-close-empty"
            onClick={this.toggleModal.bind(this, "confirmCancellationModal")}
          />
        </div>

        <div className="modal-section">
          <div className="modal-row reconcile-modal-tables">
            <div className="column-half">
              <p>
                <i className="icon-credit-card error-text icon-left middle xl-text" />
                Payments
              </p>
              <div className="table-container modal-table">
                <ReconciliationPaymentsTable
                  isIsraeli={this.props.isIsraeli}
                  selectPayment={this.props.selectPayment}
                  {...this.props.reconciliationPayments}
                  getPayments={this.props.getPayments}
                  transactionNotes={this.props.report.transactionNotes}
                  onClickTransactionNote={transactionId => {
                    this.setState(
                      {
                        transactionIdForNote: transactionId
                      },
                      () => this.toggleModal("transactionNoteModal")
                    );
                  }}
                />
              </div>
              <ReconciliationAdjustmentsList
                adjustments={this.props.reconciliationAdjustments}
                isIsraeli={this.props.isIsraeli}
                type={TransactionTypes.Withdrawal}
                updateAdjustments={this.props.updateAdjustments}
              />
              <p className="medium-text">
                Total:{" "}
                <span className="bold-text">
                  {formatCurrency(totalPayments, this.props.isIsraeli)}
                </span>
              </p>
            </div>
            <div className="column-half">
              <p>
                <i className="icon-credit-card approved-text icon-left middle xl-text" />
                Credits
              </p>
              <div>
                <div className="table-container modal-table">
                  <ReconciliationCreditsTable
                    isIsraeli={this.props.isIsraeli}
                    selectCredit={this.props.selectCredit}
                    {...this.props.reconciliationCredits}
                    getCredits={this.props.getCredits}
                    transactionNotes={this.props.report.transactionNotes}
                    onClickTransactionNote={transactionId => {
                      this.setState(
                        {
                          transactionIdForNote: transactionId
                        },
                        () => this.toggleModal("transactionNoteModal")
                      );
                    }}
                  />
                </div>
              </div>
              <ReconciliationAdjustmentsList
                adjustments={this.props.reconciliationAdjustments}
                isIsraeli={this.props.isIsraeli}
                type={TransactionTypes.Deposit}
                updateAdjustments={this.props.updateAdjustments}
              />
              <p className="medium-text">
                Total:{" "}
                <span className="bold-text">
                  {formatCurrency(totalCredits, this.props.isIsraeli)}
                </span>
              </p>
            </div>
          </div>
        </div>

        <div className="text-right balances">
          <p className="medium-text">
            Beginning Balance:{" "}
            <span className="bold-text">
              {formatCurrency(
                this.props.report.beginningBalance,
                this.props.isIsraeli
              )}
            </span>
          </p>

          <p className="medium-text">
            Current Reconciliation Ending Balance:{" "}
            <span className="bold-text">
              {formatCurrency(
                this.props.reconciliationBalance,
                this.props.isIsraeli
              )}
            </span>
          </p>

          <p className="medium-text">       
          <i className="icon-pencil primary-hover input" onClick={this.toggleModal.bind(this, "reconciliationBankBalance")} style={{marginRight: "10px"}}></i>
            Bank Statement Balance:{" "}
            <span className="bold-text">
              {formatCurrency(
                this.props.report.statementBalance,
                this.props.isIsraeli
              )}
            </span>
          </p>
          <p className="medium-text error-text">
            Difference:{" "}
            <span className="bold-text">
              {formatCurrency(difference, this.props.isIsraeli)}
            </span>
          </p>
        </div>

        <div className="modal-footer flex-justify">
          <div className="modal-btns-left">
            <button
              className="btn btn-accent2"
              onClick={this.toggleModal.bind(this, "newOutsideTrans")}
            >
              Add Transaction
            </button>
            <button
              className="btn btn-accent2 ml-8"
              onClick={this.toggleModal.bind(this, "newAdjustmentModal")}
            >
              Add Reconciliation Adjustment
            </button>
          </div>

          {this.props.loading ? (
            <LoadingSpinner height={20} alignRight={true} />
          ) : (
            <div className="modal-btns text-right">
              <button
                className="btn btn-accent"
                onClick={this.toggleModal.bind(
                  this,
                  "confirmCancellationModal"
                )}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => this.save(false)}
              >
                Save 
              </button>
              <button
                className="btn btn-primary"
                onClick={() => this.save(true)}
              >
                Save & Close
              </button>
              <button
                className="btn btn-primary"
                disabled={!enabled}
                onClick={this.reconcile.bind(this)}
              >
                Reconcile
              </button>
              <FadeOutErrorMessage show={this.state.error} />
            </div>
          )}
        </div>

        {this.state.showModals.reconciliationInfo &&
          <EditReconciliationInfoModal
            report={this.props.report}
            invalidReportDate={this.state.invalidReportDate}
            continue={this.update.bind(this)}
            close={this.toggleModal.bind(this, "reconciliationInfo")}
          />}

        {this.state.showModals.reconciliationBankBalance &&
          <EditReconciliationBankBalanceModal
            report={this.props.report}
            isIsraeli={this.props.isIsraeli}
            continue={this.update.bind(this)}
            close={this.toggleModal.bind(this, "reconciliationBankBalance")}
          />}

        <OutsideTransactionModal
          isIsraeli={this.props.isIsraeli}
          fundId={this.props.fundId}
          onSuccess={this.reloadTables.bind(this)}
          onUpdateTransactionType={() => {}}
          show={this.state.showModals.newOutsideTrans}
          close={this.toggleModal.bind(this, "newOutsideTrans")}
        />

        <TransactionNoteModal
          close={() => {
            this.toggleModal("transactionNoteModal");
            this.setState({ transactionIdForNote: null });
          }}
          save={text => {
            const transactionNotes = { ...this.props.report.transactionNotes };
            transactionNotes[this.state.transactionIdForNote] = text || null;
            this.props.onChange("transactionNotes", transactionNotes);
          }}
          show={this.state.showModals.transactionNoteModal}
          text={
            this.props.report.transactionNotes[this.state.transactionIdForNote]
          }
        />

        <ReconciliationAdjustmentModal
          close={() => this.toggleModal("newAdjustmentModal")}
          isIsraeli={this.props.isIsraeli}
          show={this.state.showModals.newAdjustmentModal}
          submit={adjustment => {
            this.props.updateAdjustments([
              ...this.props.reconciliationAdjustments,
              adjustment
            ]);
          }}
        />

        <ConfirmationModal
          close={this.toggleModal.bind(this, "confirmCancellationModal")}
          closeOnConfirm={false}
          confirm={() => this.cancel()}
          message="Are you sure you want to cancel this reconciliation?  You will lose your progress."
          show={this.state.showModals.confirmCancellationModal}
          success={true}
          title="Cancel Reconciliation"
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/accountingComponents/fundDetails/NewBankReconciliation/BankReconiliationStep2.jsx